import React, { useState, useRef } from "react"
import { Col, Container } from "reactstrap"

import "./style.scss"
import { formatNumber } from "utils/commonUtils"
import { TrailerSize } from "constants"
import useConfiguratorCalculations from "hooks/useConfiguratorCalculations"
import { calculatePackagePricing } from "utils/calculatePackagePricing"

import arrowDown from "assets/images/arrow-down.svg"

const ProductPricingDetails = ({ data }) => {
   const {
      trailerPrice,
      totalCustomizePackagesPrice,
      totalCustomizePackagesVariationPrice,
      totalEquipmentPackagesPrice,
      totalEquipmentPackagesVariationPrice,
      totalPackagePrice,
      totalSum,
      warrantyPrice,
      downPaymentAmount,
      customerDownPaymentOffer,
   } = useConfiguratorCalculations(data)

   const selectedPackages = Object.values(data.selectedPackages)
   const selectedCustomizePackages = Object.values(
      data.allSelectedCustomizePackages
   )
   const selectedEquipmentPackages = Object.values(
      data.allSelectedEquipmentPackages
   )
   const selectedWarranty = data?.warrantyData

   const renderPricingDetail = (
      label,
      price,
      labelClass = "",
      priceClass = "",
      hasWholePackage = false
   ) => {
      return (
         <div className="pricing-detail-container">
            <div className={`pricing-label ${labelClass}`}>{label}</div>
            <div className={`pricing-section-price ${priceClass}`}>
               {price || 0}
            </div>
         </div>
      )
   }

   const renderPricingSection = ({
      isPackage,
      title,
      price,
      details = [],
      hasVariation = false,
      hasWholePackage = false,
      additionalText = "",
   }) => {
      const contentRef = useRef(null)
      const [isOpen, setIsOpen] = useState(false)

      const toggleSection = () => {
         setIsOpen(prev => !prev)
      }

      let MIN_LENGTH = 0
      let totalItemsPrice = 0
      let mainPrice = 0
      let discount = 0

      if (isPackage) {
         const result = calculatePackagePricing({
            price,
            details,
            hasWholePackage,
         })
         totalItemsPrice = result.totalItemsPrice
         mainPrice = result.mainPrice
         discount = result.discount
      }

      return (
         <div>
            <div className="d-flex justify-content-between my-1 pricing-section-header">
               <div className="pricing-section-conatiner">
                  {title && (
                     <div className="text-base font-medium text-left text-app-primary pricing-section-title">
                        {title}
                        {additionalText && (
                           <span className="ms-2 text-xs font-bold">
                              ({additionalText})
                           </span>
                        )}
                     </div>
                  )}
                  {details.length > MIN_LENGTH && (
                     <img
                        src={arrowDown}
                        alt="toggle"
                        className={`arrow-icon ${isOpen ? "rotated" : ""}`}
                        onClick={toggleSection}
                     />
                  )}
               </div>

               <div className="text-base font-medium text-app-dark-gray ml-5 bold-text">
                  {isPackage
                     ? mainPrice === 0
                        ? `$${formatNumber(totalItemsPrice)}`
                        : `$${formatNumber(mainPrice)}`
                     : `$${price}`}
               </div>
            </div>
            <div
               ref={contentRef}
               className="expandable-content"
               style={{
                  maxHeight: isOpen ? contentRef.current?.scrollHeight : 0,
                  overflow: "hidden",
                  transition: "max-height 0.3s ease",
               }}
            >
               {details.length > MIN_LENGTH &&
                  details.map((detail, index) => {
                     let price = isPackage ? detail?.itemPrice : detail?.price
                     return renderPricingDetail(
                        detail?.name || detail?.itemName,
                        // formatNumber(Number(price)),
                        `$${formatNumber(Number(price))}`,
                        hasVariation ? "text-xs text-app-dark-gray" : "",
                        hasVariation ? "text-xs text-app-dark-gray" : "",
                        hasWholePackage
                     )
                  })}

               {hasWholePackage && (
                  <>
                     {renderPricingDetail(
                        "Package Subtotal",
                        `$${formatNumber(totalItemsPrice)}`,
                        "package-label",
                        "package-price"
                     )}
                     {renderPricingDetail(
                        "Discount",
                        `-$${formatNumber(discount)}`,
                        "discount-label",
                        "discount-price"
                     )}
                     {renderPricingDetail(
                        "Package Total",
                        `$${formatNumber(mainPrice)}`,
                        "package-label",
                        "package-price"
                     )}
                  </>
               )}
            </div>
         </div>
      )
   }
   return (
      <Container className="order-details-container">
         <Col lg="12" sm="12" xs="12">
            <div className="p-2">
               <table className="invoice-table">
                  <thead className="invoice-header">
                     <tr>
                        <th>ORDER SUMMARY</th>
                        <th>DETAILS</th>
                        <th className="last-cell">AMOUNT</th>
                     </tr>
                  </thead>
                  <tbody>
                     <tr className="invoice-row">
                        <td className="body-first-cell">Trailer</td>
                        <td className="body-cell bold-text">
                           {`${TrailerSize.getDisplayTextKey(
                              data?.trailerData
                                 ?.configuratorTrailerSizeType?.[0]
                           )} ${data?.trailerData?.trailerType?.trailerType}`}
                           <br />
                           {data?.trailerData?.model}
                        </td>
                        <td className="last-cell bold-text">
                           ${formatNumber(trailerPrice)}
                        </td>
                     </tr>

                     {/* Personalization */}
                     <tr className="invoice-row">
                        <td className="body-first-cell ">Personalization(s)</td>
                        <td colSpan="2" className="body-cell">
                           {selectedCustomizePackages.map((config, index) =>
                              renderPricingSection({
                                 key: index,
                                 sectionIndex: index,
                                 title: config.name,
                                 price: config.selectedVariations?.length
                                    ? formatNumber(
                                         config.selectedVariations.reduce(
                                            (sum, v) => sum + Number(v?.price),
                                            Number(config.price)
                                         )
                                      )
                                    : formatNumber(config.price),
                                 details: config.selectedVariations?.length
                                    ? config.selectedVariations
                                    : [],
                                 hasVariation: true,
                              })
                           )}
                        </td>
                     </tr>

                     {/* packages */}
                     <tr className="invoice-row">
                        <td className="body-first-cell">Package(s)</td>
                        <td colSpan="2" className="body-cell">
                           {" "}
                           {selectedPackages.map((pkg, index) =>
                              renderPricingSection({
                                 isPackage: true,
                                 key: index,
                                 sectionIndex: index,
                                 title: pkg.packageName,
                                 price:
                                    pkg?.selectedItems?.length ===
                                    Object.keys(pkg.items)?.length
                                       ? formatNumber(
                                            Number(pkg.price).toFixed(2)
                                         )
                                       : "",
                                 details: pkg.selectedItems || [],
                                 hasVariation: !!pkg?.selectedItems?.length,
                                 hasWholePackage:
                                    pkg?.selectedItems?.length ===
                                    Object.keys(pkg.items)?.length,
                              })
                           )}
                        </td>
                     </tr>

                     {/* Equipment */}
                     <tr className="invoice-row">
                        <td className="body-first-cell">Equipment(s)</td>
                        <td colSpan="2" className="body-cell">
                           {selectedEquipmentPackages.map((config, index) =>
                              renderPricingSection({
                                 key: index,
                                 sectionIndex: index,
                                 title: config?.name,
                                 price: config.selectedVariations?.length
                                    ? formatNumber(
                                         config.selectedVariations.reduce(
                                            (sum, v) => sum + Number(v?.price),
                                            Number(config.price)
                                         )
                                      )
                                    : formatNumber(config.price),
                                 details: config.selectedVariations?.length
                                    ? config.selectedVariations
                                    : [],
                                 hasVariation: true,
                              })
                           )}
                        </td>
                     </tr>
                     <tr className="invoice-row">
                        <td className="body-first-cell">Warranty(ies)</td>
                        <td colSpan="2" className="body-cell">
                           {selectedWarranty?.length
                              ? selectedWarranty.map((warranty, index) =>
                                   renderPricingSection({
                                      key: index,
                                      sectionIndex: index,
                                      title: warranty?.configuratorWarranties
                                         ?.name,
                                      price: formatNumber(
                                         warranty?.configuratorWarranties?.price
                                      ),
                                      details: [],
                                      additionalText: warranty
                                         ?.configuratorWarranties?.isDefault
                                         ? "Included"
                                         : "",
                                   })
                                )
                              : ""}
                        </td>
                     </tr>
                     <tr>
                        <td colSpan="3">
                           <div className="footer-total">
                              <label>Total Price</label>
                              <p>${formatNumber(totalSum)}</p>
                           </div>
                        </td>
                     </tr>
                     {!!downPaymentAmount && !!customerDownPaymentOffer && (
                        <tr>
                           <td colSpan="3">
                              <div className="footer-deposit">
                                 <label>Deposit (${downPaymentAmount})</label>
                                 <p>(${customerDownPaymentOffer}) payable</p>
                              </div>
                           </td>
                        </tr>
                     )}
                  </tbody>
               </table>
            </div>
         </Col>
      </Container>
   )
}

export default React.memo(ProductPricingDetails)
